import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/*
 * Little & Sweet aanpassing (okt 2026):
 * Het zoomvenster toont op elk scherm één foto tegelijk, met pijlen links en rechts
 * en de pijltjestoetsen op het toetsenbord. Origineel Horizon-gedrag op desktop was
 * alle foto's onder elkaar in een scrollvenster.
 */

const LS_ARROW_PREV =
  '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M15 5l-7 7 7 7"/></svg>';
const LS_ARROW_NEXT =
  '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M9 5l7 7-7 7"/></svg>';

const LS_STYLES = `
  zoom-dialog .ls-zoom-arrow {
    position: fixed;
    top: 50%;
    transform: translateY(-50%);
    z-index: var(--layer-raised, 3);
    width: 48px;
    height: 48px;
    padding: 0;
    margin: 0;
    border: 0;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgb(255 255 255 / 0.92);
    color: #5c3a2e;
    box-shadow: 0 2px 10px rgb(0 0 0 / 0.12);
    cursor: pointer;
    transition: background-color 0.2s ease, box-shadow 0.2s ease;
  }
  zoom-dialog .ls-zoom-arrow:hover {
    background-color: #fff;
    box-shadow: 0 3px 14px rgb(0 0 0 / 0.18);
  }
  zoom-dialog .ls-zoom-arrow:focus-visible {
    outline: 2px solid #5c3a2e;
    outline-offset: 3px;
  }
  zoom-dialog .ls-zoom-arrow--prev { left: 24px; }
  zoom-dialog .ls-zoom-arrow--next { right: calc(var(--thumbnail-width, 80px) + 64px); }

  @media screen and (max-width: 749px) {
    zoom-dialog .ls-zoom-arrow { width: 40px; height: 40px; }
    zoom-dialog .ls-zoom-arrow--prev { left: 10px; }
    zoom-dialog .ls-zoom-arrow--next { right: 10px; }
  }

  @media screen and (min-width: 750px) {
    zoom-dialog dialog.dialog-zoomed-gallery__dialog {
      overflow: hidden;
    }
    zoom-dialog .dialog-zoomed-gallery {
      display: flex;
      flex-direction: row;
      height: 100vh;
      height: 100dvh;
      overflow-x: auto;
      overflow-y: hidden;
      scroll-snap-type: x mandatory;
      overscroll-behavior-x: contain;
      scrollbar-width: none;
    }
    zoom-dialog .dialog-zoomed-gallery::-webkit-scrollbar {
      display: none;
    }
    zoom-dialog .dialog-zoomed-gallery > li {
      flex: 0 0 100%;
      width: 100%;
      height: 100%;
      max-height: none;
      scroll-snap-align: start;
      scroll-snap-stop: always;
      display: flex;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
      padding: 48px calc(var(--thumbnail-width, 80px) + 128px) 48px 96px;
    }
    zoom-dialog .dialog-zoomed-gallery > li > drag-zoom-wrapper,
    zoom-dialog .dialog-zoomed-gallery > li > .product-media {
      width: 100%;
      height: 100%;
      max-width: 100%;
      max-height: 100%;
    }
    zoom-dialog .dialog-zoomed-gallery > li .product-media {
      aspect-ratio: auto;
      width: 100%;
      height: 100%;
    }
    zoom-dialog .dialog-zoomed-gallery > li.product-media-container--image img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }
`;

function injectLsStyles() {
  if (document.getElementById('ls-zoom-dialog-styles')) return;
  const style = document.createElement('style');
  style.id = 'ls-zoom-dialog-styles';
  style.textContent = LS_STYLES;
  document.head.append(style);
}

/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /** @type {HTMLElement | null} */
  #gallery = null;

  #currentIndex = 0;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    this.#setupSlider();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.#gallery?.removeEventListener('scroll', this.handleScroll);
  }

  #setupSlider() {
    const { dialog } = this.refs;
    if (!dialog) return;

    injectLsStyles();

    this.#gallery = dialog.querySelector('.dialog-zoomed-gallery');
    this.#gallery?.addEventListener('scroll', this.handleScroll);

    const mediaCount = Array.isArray(this.refs.media) ? this.refs.media.length : 0;
    if (mediaCount < 2 || dialog.querySelector('.ls-zoom-arrow')) return;

    const makeArrow = (direction, label, icon) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `ls-zoom-arrow ls-zoom-arrow--${direction}`;
      button.setAttribute('aria-label', label);
      button.innerHTML = icon;
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (direction === 'prev') this.previous();
        else this.next();
      });
      return button;
    };

    dialog.append(makeArrow('prev', 'Previous image', LS_ARROW_PREV), makeArrow('next', 'Next image', LS_ARROW_NEXT));
  }

  /**
   * Scrolls the gallery horizontally to a given image. Returns false when the gallery
   * is not laid out horizontally, so the caller can fall back to the original behavior.
   * @param {number} index
   * @param {ScrollBehavior} behavior
   */
  #scrollGalleryTo(index, behavior) {
    const gallery = this.#gallery;
    if (!gallery || gallery.scrollWidth <= gallery.clientWidth + 1) return false;

    gallery.scrollTo({ left: index * gallery.clientWidth, behavior });
    return true;
  }

  previous() {
    const count = this.refs.media.length;
    if (count < 2) return;
    const wrapping = this.#currentIndex === 0;
    const index = wrapping ? count - 1 : this.#currentIndex - 1;
    this.selectThumbnail(index, { behavior: wrapping || prefersReducedMotion() ? 'instant' : 'smooth' });
  }

  next() {
    const count = this.refs.media.length;
    if (count < 2) return;
    const wrapping = this.#currentIndex >= count - 1;
    const index = wrapping ? 0 : this.#currentIndex + 1;
    this.selectThumbnail(index, { behavior: wrapping || prefersReducedMotion() ? 'instant' : 'smooth' });
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];
    this.#currentIndex = index;

    const open = () => {
      dialog.showModal();

      targetThumbnail?.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'nearest' });

      if (!this.#scrollGalleryTo(index, 'instant')) {
        targetImage?.scrollIntoView({ behavior: 'instant' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog or the gallery, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    if (activeIndex >= 0) this.#currentIndex = activeIndex;

    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Escape closes the dialog, the left and right arrow keys move between images.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previous();
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
      return;
    }

    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    this.#currentIndex = index;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      if (!this.#scrollGalleryTo(index, options.behavior)) {
        targetImage.scrollIntoView({
          behavior: options.behavior,
        });
      }

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
